import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { PhaseFrise } from "@/components/procedure/PhaseFrise";
import {
  ajouterMembreTribunal,
  constituerTribunal,
  getCompositionTribunal,
  getDossier,
  retirerMembreTribunal,
} from "@/lib/apiProcedure";
import type { CompositionTribunal, DossierProcedure } from "@/types/procedure";

const ROLES = [
  { value: "ARBITRE", label: "Arbitre" },
  { value: "ARBITRE_PRESIDENT", label: "Président" },
  { value: "ARBITRE_COARBITRE", label: "Co-arbitre" },
  { value: "SECRETAIRE", label: "Secrétaire" },
];

export default function TribunalPage() {
  const [, params] = useRoute<{ id: string }>("/greffe/cases/:id/tribunal");
  const id = params?.id;
  const [, navigate] = useLocation();

  const [dossier, setDossier] = useState<DossierProcedure | null>(null);
  const [composition, setComposition] = useState<CompositionTribunal | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [newMemberId, setNewMemberId] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("ARBITRE");

  const reload = async () => {
    if (!id) return;
    const [resDossier, resComp] = await Promise.all([
      getDossier(id),
      getCompositionTribunal(id),
    ]);
    setDossier(resDossier.data);
    setComposition(resComp.data);
  };

  useEffect(() => {
    if (!id) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAjouter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newMemberId.trim()) return;
    await ajouterMembreTribunal(id, {
      user_id: newMemberId.trim(),
      member_role: newMemberRole,
    });
    setNewMemberId("");
    await reload();
  };

  const handleRetirer = async (userId: string) => {
    if (!id || !confirm("Retirer ce membre ?")) return;
    await retirerMembreTribunal(id, userId);
    await reload();
  };

  const handleConstituer = async () => {
    if (!id) return;
    try {
      await constituerTribunal(id);
      navigate(`/cases/${id}`);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Erreur lors de la constitution.";
      alert(detail);
    }
  };

  if (loading || !dossier || !composition)
    return <div className="p-6">Chargement…</div>;

  if (dossier.phase_procedurale !== "RECEVABLE") {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <PhaseFrise currentPhase={dossier.phase_procedurale} />
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-amber-800">
          La constitution n'est disponible qu'une fois le dossier déclaré{" "}
          <strong>RECEVABLE</strong> (phase actuelle :{" "}
          {dossier.phase_procedurale ?? "—"}).
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <PhaseFrise currentPhase={dossier.phase_procedurale} />

      <div className="flex justify-end">
        <Link
          href={`/greffe/cases/${id}/parties`}
          className="text-sm text-indigo-600 hover:underline"
        >
          Enrôler les parties &amp; conseils →
        </Link>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">
            Constitution du tribunal (AUA art. 6-7)
          </h2>
          <span className="text-sm text-gray-500">
            Type : {dossier.tribunal_type || "non défini"}
          </span>
        </div>

        <form
          onSubmit={handleAjouter}
          className="flex flex-wrap gap-3 mb-8 p-4 bg-gray-50 rounded"
        >
          <input
            type="text"
            placeholder="Identifiant (sub) de l'arbitre"
            value={newMemberId}
            onChange={(e) => setNewMemberId(e.target.value)}
            required
            className="flex-1 min-w-[200px] rounded border p-2"
          />
          <select
            value={newMemberRole}
            onChange={(e) => setNewMemberRole(e.target.value)}
            className="rounded border p-2"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Ajouter
          </button>
        </form>

        <table className="w-full text-left mb-6">
          <thead>
            <tr className="border-b text-sm text-gray-500">
              <th className="py-2">Membre</th>
              <th className="py-2">Rôle</th>
              <th className="py-2">Indépendance</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {composition.membres.map((m) => (
              <tr key={m.id} className="border-b">
                <td className="py-2 font-mono text-xs">{m.user_id}</td>
                <td className="py-2 text-sm text-gray-600">{m.member_role}</td>
                <td className="py-2">
                  {m.independence_signed ? (
                    <span className="text-green-600 font-bold">✓ Signée</span>
                  ) : (
                    <span className="text-red-500 font-bold">✗ En attente</span>
                  )}
                </td>
                <td className="py-2">
                  <button
                    onClick={() => handleRetirer(m.user_id)}
                    className="text-red-500 hover:underline text-sm"
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
            {composition.membres.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-sm text-gray-400 italic">
                  Aucun membre affecté.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!composition.prete_pour_constitution &&
          composition.blocages.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
              <h4 className="font-bold mb-2">Bloquants avant constitution :</h4>
              <ul className="list-disc list-inside text-sm">
                {composition.blocages.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}

        <button
          onClick={handleConstituer}
          disabled={!composition.prete_pour_constitution}
          className="w-full bg-green-600 text-white py-3 rounded-md font-bold text-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-green-700 transition"
        >
          Constituer le tribunal et ouvrir l'instruction
        </button>
      </div>
    </div>
  );
}
